import client from '../client';
import type { User, UserPreferences } from '../../types/auth';
import type { ApiResponse } from '../../types/api';

export const authApi = {
  me: async (): Promise<User> => {
    const res = await client.get<ApiResponse<User>>('/dashboard/auth/me');
    return res.data.data!;
  },

  /**
   * Patch the current user's account-level preferences. Merges server-side —
   * only the keys you pass are touched.
   */
  updatePreferences: async (patch: Partial<UserPreferences>): Promise<UserPreferences> => {
    const res = await client.patch<ApiResponse<UserPreferences>>('/dashboard/auth/preferences', patch);
    return res.data.data ?? {};
  },

  /**
   * Update the signed-in merchant's own profile. Only the keys you pass are
   * touched. `avatar` is a base64 data URL; the server writes it to
   * usersAttachments/ on the CDN and stores the basename, so 00acc's own
   * profile page keeps resolving the same image.
   *
   * Phone is NOT settable here on purpose — see sendPhoneOtp/verifyPhoneOtp.
   */
  updateProfile: async (patch: {
    username?: string;
    nickname?: string;
    bio?: string;
    avatar?: string;
  }): Promise<Pick<User, 'username' | 'nickname' | 'bio' | 'profileIcon'>> => {
    const res = await client.post<ApiResponse<Pick<User, 'username' | 'nickname' | 'bio' | 'profileIcon'>>>(
      '/dashboard/auth/profile',
      patch,
    );
    return res.data.data!;
  },

  /**
   * Start adding or changing the account phone: sends a 4-digit code to the
   * NEW number over WhatsApp (Gupshup, with an SMS fallback). The number is
   * only written to the account once verifyPhoneOtp() accepts the code.
   */
  sendPhoneOtp: async (phoneNumber: string, phoneCountryCode: string): Promise<void> => {
    await client.post<ApiResponse>('/dashboard/auth/phone/send-otp', {
      phoneNumber,
      phoneCountryCode,
    });
  },

  /** Confirm the code from sendPhoneOtp() and commit the new number. */
  verifyPhoneOtp: async (code: string): Promise<{ phoneNumber: string; phoneCountryCode: string }> => {
    const res = await client.post<ApiResponse<{ phoneNumber: string; phoneCountryCode: string }>>(
      '/dashboard/auth/phone/verify',
      { code },
    );
    return res.data.data!;
  },

  /**
   * Tell zero to drop its cached `accounts` doc for the current user.
   * Call this after writing to the user document via a different service
   * (e.g. 00acc's /editprofile) so the next /me returns fresh data
   * instead of a 10-min-stale cache entry.
   */
  refreshCache: async (): Promise<void> => {
    await client.post<ApiResponse>('/dashboard/auth/refresh-cache');
  },

  /**
   * Revoke the current session and clear its cookie on our own backend.
   * Idempotent and self-contained — no 00acc redirect.
   */
  signout: async (): Promise<void> => {
    await client.post<ApiResponse>('/dashboard/auth/signout');
  },
};
